/**
 * The PUBLISH-TIME pricing rule (Chapter 10, F3).
 *
 * # What went wrong
 *
 * A tester lowered the quantity from 250 to 100 and the price did not move.
 * `getTieredPrice` falls back to the option's flat `price` whenever no band
 * carries a price, and the Creation Lab keeps the tier table collapsed under
 * "Advanced" while stamping a flat `price` (or a table of five zeros) by
 * default. So every box authored in the admin priced identically at every
 * quantity — silently, with no signal anywhere that the staffel was missing.
 *
 * # The rule
 *
 * An option must not reach the public site charging money with no quantity
 * staffel *unless its author said so on purpose*. So publishing is blocked
 * when an option
 *
 *   1. charges money (`price > 0`), AND
 *   2. resolves no per-band price (no `tieredPrices`, or every band is 0), AND
 *   3. does not carry the explicit `noQuantityDiscount` opt-out.
 *
 * Free items (`price === 0` — every "Other…" option) pass with no flag, and the
 * opt-out keeps genuinely flat items publishable, so the gate can never become
 * unpassable (Chapter-10 brief §3 constraint 6).
 *
 * # Why "all bands zero" counts as no tiers
 *
 * Because it BEHAVES as no tiers: `getTieredPrice`'s downward walk skips zeros
 * and returns the flat price. A rule that only checked for the presence of the
 * object would pass the admin's own "Final touch" template
 * (`price: 0.5` + five zeros) — i.e. it would pass the exact reported bug.
 *
 * # Why not "the price must VARY across bands"
 *
 * Because the seeded Spring sheet legitimately prices some items identically in
 * every band (`easter-eggs` is 4.43 at 25 and at 250). Authoring the same
 * number five times is a deliberate statement of flatness from the real price
 * sheet; having no numbers at all is an omission. The gate distinguishes the
 * two.
 *
 * This module is PURE and lives in the shared schema package on purpose: the
 * store enforces it on publish and the admin editor renders the same gaps
 * per-option (Chapter-10 P2b). One policy, two consumers, no second copy —
 * the same instinct as P1's `uiRoleRequirements`.
 */

import { TIER_BAND_KEYS } from './pricingBands.js';
import type { TieredPriceOptionLike } from './tieredPrice.js';

/** Stable error code the HTTP layer maps to a 409. */
export const MISSING_TIER_PRICES_CODE = 'MISSING_TIER_PRICES';

/**
 * The minimal option shape the rule reads. Structural (not the full `Option`)
 * so the public app's adapted types satisfy it too — same convention as
 * `optionSemantics.ts`.
 *
 * `id` / `label` are declared OPTIONAL on purpose. `apps/admin` compiles with
 * `strict: false`, under which zod's `z.infer` marks every property of
 * `FlowDoc` optional; a required `id` here would make the real document type
 * unassignable in that workspace, and casting product code to satisfy a
 * type-checker flag is worse than a tolerant boundary. The gap builder
 * substitutes a positional description when either is missing, so the message
 * stays actionable either way.
 */
export interface TierPricedOptionLike extends TieredPriceOptionLike {
  readonly id?: string;
  readonly label?: string;
  /**
   * Author's explicit declaration that this option's unit price does NOT vary
   * with quantity. Set it and the option needs no bands.
   */
  readonly noQuantityDiscount?: boolean;
}

/** The minimal step shape — the gap message names the step it lives in. */
export interface TierPricedStepLike {
  readonly id?: string;
  readonly title?: string;
  readonly options?: readonly TierPricedOptionLike[];
}

export interface TierPricedFlowLike {
  /** Optional for the same `strict: false` reason as the fields above. */
  readonly steps?: readonly TierPricedStepLike[];
}

/** One offending option, named well enough for an author to go and fix it. */
export interface TierPriceGap {
  readonly stepId: string;
  readonly stepTitle: string;
  readonly optionId: string;
  readonly optionLabel: string;
  /** The flat price that would be charged at every quantity. */
  readonly price: number;
}

/**
 * True when at least one band carries a non-zero price — i.e. when
 * `getTieredPrice` can actually resolve a quantity-dependent price.
 */
export function hasEffectiveTierPrices(option: TierPricedOptionLike): boolean {
  const tiers = option.tieredPrices;
  if (!tiers) return false;
  return TIER_BAND_KEYS.some((key) => {
    const value = tiers[key];
    return typeof value === 'number' && value > 0;
  });
}

/** True when this option blocks publish: it charges money, prices no band, and has no opt-out. */
export function optionNeedsTierPrices(option: TierPricedOptionLike): boolean {
  if (option.noQuantityDiscount === true) return false;
  if (!((option.price ?? 0) > 0)) return false;
  return !hasEffectiveTierPrices(option);
}

/**
 * Every publish-blocking option in a flow, in document order.
 *
 * A step/option missing its `id` or `label` is described positionally
 * ("step 2", "option 3") rather than dropped — a nameless option is exactly the
 * half-authored case the author most needs pointing at.
 */
export function findTierPriceGaps(flow: TierPricedFlowLike): TierPriceGap[] {
  const gaps: TierPriceGap[] = [];
  (flow.steps ?? []).forEach((step, stepIndex) => {
    (step.options ?? []).forEach((option, optionIndex) => {
      if (!optionNeedsTierPrices(option)) return;
      gaps.push({
        stepId: step.id ?? `step ${stepIndex + 1}`,
        stepTitle: step.title ?? step.id ?? `step ${stepIndex + 1}`,
        optionId: option.id ?? `option ${optionIndex + 1}`,
        optionLabel: option.label ?? option.id ?? `option ${optionIndex + 1}`,
        price: option.price ?? 0,
      });
    });
  });
  return gaps;
}

/**
 * The operator-facing message. Names EVERY offending option (label, id, step)
 * — a generic "validation failed" would leave an author hunting through a
 * 30-option box — and states both ways out: author the bands, or tick the
 * opt-out.
 */
export function describeTierPriceGaps(gaps: readonly TierPriceGap[]): string {
  if (gaps.length === 0) return '';
  const listed = gaps
    .map((g) => `"${g.optionLabel}" (${g.optionId}) in step "${g.stepTitle}" — flat €${g.price}`)
    .join('; ');
  const one = gaps.length === 1;
  const subject = one ? '1 option charges' : `${gaps.length} options charge`;
  const have = one ? 'has' : 'have';
  const its = one ? 'its' : 'their';
  return (
    `Cannot publish: ${subject} money but ${have} no quantity price bands, ` +
    `so ${its} price would not change with the order quantity. ` +
    `Add tier prices, or set "noQuantityDiscount" on each option that genuinely has no ` +
    `quantity discount. Offending options: ${listed}.`
  );
}
